/**
 * Detect overlapping same-store visit intervals on the timesheet (#2070, Layer
 * B of #2062 — the SAFE, presentational half).
 *
 * A physical store visit is a single time interval, so two visits at the SAME
 * store whose time bounds overlap (e.g. an active/never-closed check-in plus a
 * later completed visit) are anomalous and would double-count time. We surface
 * them with a warning rather than silently double-counting or auto-mutating the
 * records (auto-close/merge is deliberately left to a server-side, audited step).
 *
 * This is pure + precise so it can be unit-tested and won't false-positive on a
 * legitimate non-overlapping revisit (completed earlier, checked in again later).
 */

export interface OverlapEntry {
  id: number;
  /** Site key — same store label groups together (e.g. "PX #1502"). */
  storeKey: string;
  /** Start in minutes-since-midnight; null if unparseable (skipped). */
  startMin: number | null;
  /** End in minutes-since-midnight; ignored when `open` is true. */
  endMin: number | null;
  /** True for an active / never-closed visit — its interval extends to "now". */
  open: boolean;
}

/** Two half-open intervals [aS,aE) and [bS,bE) overlap iff aS < bE && bS < aE. */
function intervalsOverlap(aS: number, aE: number, bS: number, bE: number): boolean {
  return aS < bE && bS < aE;
}

/**
 * Return the set of entry ids that overlap at least one OTHER entry at the same
 * store. An `open` (active) entry is treated as [start, +Infinity) so a later
 * completed visit at the same store counts as overlapping it.
 */
export function findOverlappingEntryIds(entries: OverlapEntry[]): Set<number> {
  const overlapping = new Set<number>();
  // Group by store, keeping only entries with a parseable start.
  const byStore = new Map<string, OverlapEntry[]>();
  for (const e of entries) {
    if (e.startMin === null) continue;
    const arr = byStore.get(e.storeKey);
    if (arr) arr.push(e);
    else byStore.set(e.storeKey, [e]);
  }

  for (const group of byStore.values()) {
    if (group.length < 2) continue;
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const a = group[i];
        const b = group[j];
        const aStart = a.startMin as number;
        const bStart = b.startMin as number;
        const aEnd = a.open || a.endMin === null ? Number.POSITIVE_INFINITY : a.endMin;
        const bEnd = b.open || b.endMin === null ? Number.POSITIVE_INFINITY : b.endMin;
        if (intervalsOverlap(aStart, aEnd, bStart, bEnd)) {
          overlapping.add(a.id);
          overlapping.add(b.id);
        }
      }
    }
  }
  return overlapping;
}
